/**
 * Mock MyndScape — n-back cognitive task in telerehab mode.
 *
 * Real MyndScape integration: runs the actual n-back task UI, emits
 * trial-level data per trial (already shipped per FEA-012). In
 * telerehab mode, trainer can see live accuracy + RT mean.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DemoGuard, NotInProduction, isDemoHostAllowed } from "../components/DemoBanner.js";

const STIMS = ["A", "B", "C", "D", "E", "F"];
const N = 2; // 2-back

interface Trial {
  stim: string;
  isMatch: boolean;
  responseMs?: number;
  correct?: boolean;
}

export default function MyndScapeDemoSubapp() {
  const [search] = useSearchParams();
  const room = search.get("room");
  const isTr = search.get("telerehab") === "1";

  const [stim, setStim]       = useState<string | null>(null);
  const [history, setHistory] = useState<Trial[]>([]);
  const [showing, setShowing] = useState(false);
  const [running, setRunning] = useState(true);
  const stimAtRef = useRef<number>(0);
  const bcRef     = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!isTr || !room) return;
    if (!isDemoHostAllowed()) return;
    const bc = new BroadcastChannel(`tr-${room}`);
    bcRef.current = bc;
    bc.onmessage = (ev) => {
      const m = ev.data;
      if (m.type === "start" || m.type === "resume") setRunning(true);
      if (m.type === "pause" || m.type === "stop")   setRunning(false);
    };
    bc.postMessage({ type: "subapp_state", state: "ready", task: "n-back" });
    return () => bc.close();
  }, [isTr, room]);

  // Trial loop — show stim 1.5s, ISI 1.5s
  useEffect(() => {
    if (!running) return;
    const tick = () => {
      const stims = STIMS;
      const next = stims[Math.floor(Math.random() * stims.length)];
      setStim(next);
      setShowing(true);
      stimAtRef.current = Date.now();

      setTimeout(() => {
        // After 1500ms, decide if no response = miss
        setHistory(h => {
          const last = h[h.length - 1];
          if (last && last.responseMs === undefined) {
            // Mark as miss if was a match
            const correct = !last.isMatch; // miss is correct only if not match
            return [...h.slice(0, -1), { ...last, correct }];
          }
          return h;
        });
        setShowing(false);
      }, 1500);
    };

    // Determine match (against 2 trials ago)
    setHistory(h => {
      const stims = STIMS;
      const next = stims[Math.floor(Math.random() * stims.length)];
      const isMatch = h.length >= N && h[h.length - N].stim === next;
      return [...h, { stim: next, isMatch }];
    });

    const interval = setInterval(tick, 3000);
    tick();
    return () => clearInterval(interval);
  }, [running]);

  // Emit aggregated metrics every 2s. demo:true so reports skip them.
  useEffect(() => {
    if (!isDemoHostAllowed()) return;
    const t = setInterval(() => {
      const completed = history.filter(h => h.correct !== undefined);
      if (completed.length === 0) return;
      const correct  = completed.filter(h => h.correct).length;
      const accuracy = correct / completed.length;
      const rts = completed.filter(h => h.responseMs !== undefined).map(h => h.responseMs!);
      const meanRt = rts.length ? rts.reduce((a, b) => a + b, 0) / rts.length : 0;

      const bc = bcRef.current;
      if (!bc) return;
      const ts = Date.now();
      bc.postMessage({ type: "metric", source: "task.accuracy",  value: accuracy,         ts, demo: true });
      bc.postMessage({ type: "metric", source: "task.rt_ms",     value: meanRt,           ts, demo: true });
      bc.postMessage({ type: "metric", source: "task.completed", value: completed.length, ts, demo: true });
      bc.postMessage({ type: "metric", source: "reward",         value: accuracy,         ts, demo: true });
    }, 2000);
    return () => clearInterval(t);
  }, [history]);

  function respond() {
    if (!stim || !showing) return;
    const rt = Date.now() - stimAtRef.current;
    setHistory(h => {
      const last = h[h.length - 1];
      if (!last || last.responseMs !== undefined) return h;
      return [...h.slice(0, -1), {
        ...last,
        responseMs: rt,
        correct: last.isMatch, // user pressed = thought it's a match
      }];
    });
  }

  // Spacebar = "match" response
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.code === "Space") { e.preventDefault(); respond(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stim, showing]);

  if (!isDemoHostAllowed()) return <NotInProduction subapp="MyndScape" />;

  if (!isTr) {
    return <main className="page page--auth"><h1>MyndScape Demo</h1><p className="muted">需要 ?telerehab=1&amp;room=...</p></main>;
  }

  const completed = history.filter(h => h.correct !== undefined);
  const correct = completed.filter(h => h.correct).length;
  const acc = completed.length ? (correct / completed.length * 100).toFixed(0) : "—";

  return (
    <DemoGuard subapp="MyndScape · n-back">
      <div style={{
        padding: "8px var(--s-5)", display: "flex", justifyContent: "space-between",
        background: "rgba(0,0,0,0.4)", color: "var(--paper)", fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        flexShrink: 0,
      }}>
        <span style={{ letterSpacing: "0.1em" }}>MYNDSCAPE · {N}-BACK · TELEREHAB</span>
        <span>room {room?.slice(0, 12)}</span>
        <span style={{ color: running ? "var(--olive)" : "var(--warning)" }}>● {running ? "running" : "paused"}</span>
      </div>

      <div style={{ flex: 1, position: "relative", display: "flex",
                    alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        {showing && stim ? (
          <div style={{
            fontFamily: "var(--display)",
            fontSize: 240,
            fontWeight: 400,
            letterSpacing: "-0.05em",
            color: "var(--paper)",
            lineHeight: 0.9,
            cursor: "pointer",
          }} onClick={respond}>{stim}</div>
        ) : (
          <div style={{ width: 14, height: 14, background: "var(--paper-deep)", borderRadius: "50%" }} />
        )}
        <div style={{
          marginTop: "var(--s-7)",
          color: "var(--paper-deep)", fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
          letterSpacing: "0.1em",
        }}>
          看到跟「2 個前」一樣的字母 → 按 SPACE 或點擊
        </div>
      </div>

      <div style={{
        padding: "var(--s-3) var(--s-5)", display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--s-5)",
        fontFamily: "var(--mono)", fontSize: "var(--t-sm)",
        background: "rgba(0,0,0,0.3)",
      }}>
        <div><div style={{ color: "var(--paper-deep)", fontSize: "var(--t-xs)" }}>trial</div>
             <div style={{ fontSize: "var(--t-md)" }}>{history.length}</div></div>
        <div><div style={{ color: "var(--paper-deep)", fontSize: "var(--t-xs)" }}>accuracy</div>
             <div style={{ fontSize: "var(--t-md)", color: "var(--rust)" }}>{acc}%</div></div>
        <div><div style={{ color: "var(--paper-deep)", fontSize: "var(--t-xs)" }}>completed</div>
             <div style={{ fontSize: "var(--t-md)" }}>{completed.length}</div></div>
      </div>
    </DemoGuard>
  );
}
